<template>
    <div class="hts-distributing">
        <div
            class="hts-distributing-list hts-distributing__list flex flex-col transition-all w-[460px]"
            :class="{ hidden: !showJourey, flex: showJourey }">
            <div class="hts-distributing__sidebar header box-border p-2 pr-1">
                <hts-map-icon-journey class="float-right" />

                <h3>
                    Danh sách chặng cần điều phối:
                </h3>
                <hts-form-input
                    v-model="journeyFilters.search"
                    class="flex-initial w-full mt-2"
                    :placeholder="$t('labels.findJourney')">
                    <template #prefix>
                        <hi-magnifying-glass-icon class="h-6 w-6 ml-2 text-gray-500" />
                    </template>
                </hts-form-input>
            </div>

            <div class="hts-distributing-list__content flex-1 mt-2 overflow-hidden content">
                <hts-ui-tabs
                    v-model="journeyStatusViewing"
                    :tabs="journeyTabs"
                    size="small" />

                <journey-list
                    ref="journeyListComponent"
                    v-model:selected="selectedJourneys"
                    :items="journeys"
                    :loading="journeyLoading"
                    @click="selectJourney">
                    <template #actions="{ item, hover }">
                        <div class="flex-initial flex flex-col justify-center items-center p-2 text-right max-w-[100px]">
                            <span
                                class="text-base select-none cursor-pointer text-info-500 hover:text-info-600 underline"
                                @click="openJourney(item)">
                                Xem chi tiết
                            </span>
                            <div
                                v-if="item.vehicleId"
                                class="relative mt-2 rounded border border-green-500 text-green-500 px-1 text-left">
                                <span>
                                    {{ item.vehicleLicensePlate }}
                                </span>
                            </div>
                            <div
                                v-else-if="item.vehicleTempId"
                                class="relative mt-2 rounded border border-orange-500 text-orange-500 px-1 text-left">
                                <span>
                                    Xe dự kiến: {{ item.vehicleTempLicensePlate }}
                                </span>
                            </div>
                            <hts-form-button
                                v-if="!item.vehicleId"
                                class="flex-initial mt-2"
                                :class="{ invisible: !hover }"
                                color="info"
                                type="button"
                                size="small"
                                :outline="false"
                                @click="handleManualSchedule(item)">
                                <span class="text-sm whitespace-nowrap">
                                    Chọn xe
                                </span>
                            </hts-form-button>
                        </div>
                    </template>
                </journey-list>
            </div>
        </div>

        <div class="hts-distributing__detail a">
            <journey-schedule
                ref="journeyScheduleComponent"
                @change="refresh" />
        </div>

        <!-- Dialogs -->
        <journey-info
            ref="journeyInfoComponent"
            :api="$urlPartner.journey.get" />
    </div>
</template>

<script setup lang="ts">
import JourneyInfo from '../-components/journey-info.vue'
import JourneyList from './-components/journey-list.vue'
import JourneySchedule from './-components/journey-schedule.vue'

const { $dayjs, $event, $formatters } = useNuxtApp()
const { system, distributingPage, map } = $store()
const route = useRoute()
const router = useRouter()
const checkAll = ref(false)
const journeyFilters = computed({
  get: () => distributingPage.getJourneyFilters,
  set: (val: any) => {
    distributingPage.setJourneyFilters(val)
  }
})

const showJourey = computed(() => {
  return map.getShowJourney
})

const journeyLoading = ref(true)
const journeys = ref<any[]>([])
const journeyLength = computed(() => journeys.value.length)
const selectedJourneys = ref<any[]>([])
const selected = computed(() => selectedJourneys.value.length > 0)
const journeyStatusViewing = ref('ALL')

const nextScheduleDatetime = computed(() => {
  const now = $dayjs().minute(0).second(0)
  const startDatetime = $dayjs().hour(system.scheduleSettings.start).minute(0).second(0)
  const endDatetime = $dayjs().hour(system.scheduleSettings.end).minute(0).second(0)
  if (now < startDatetime) {
    return startDatetime
  }
  if (now < endDatetime) {
    return endDatetime
  }
  return startDatetime.add(1, 'day')
})
const nextScheduleConfirmDatetime = computed(() => {
  return nextScheduleDatetime.value.add(system.scheduleSettings.confirmationTime, 'm')
})
onMounted(() => {
  automationActions()
  getJourneys()
  $event.listen(`c-${company.value._id}-journey-schedule-change`, (data: any) => {
    setDataScheduleChange(data)
  })
  $event.listen(`c-${company.value._id}-change-vehicle-change-ready`, (data: any) => {
    setDataVehicleChange(data)
  })
})

const action = computed(() => route.query.action)
watch(
  () => action.value,
  () => automationActions()
)

const automationActions = () => {
  const action = route.query.action || ''
  switch (action) {
  case 'open-schedule':
    openScheduleInfo()
    break
  }
  nextTick(() => {
    router.replace({ query: { action: undefined } })
  })
}

watch(
  () => checkAll.value,
  (val: boolean) => {
    handleCheckAll(val)
  }
)

watch(
  () => journeyStatusViewing.value,
  (val: string) => {
    refresh()
  }
)

watch(
  () => journeyFilters.value.search,
  (val: string) => {
    refresh()
  }
)

const refresh = $lodash.debounce(() => {
  journeys.value = []
  checkAll.value = false
  selectedJourneys.value = []
  getJourneys()
}, 500)

const getJourneys = () => {
  journeyLoading.value = true
  $api($urlPartner.journey.getJourneys, {
    query: {
      skip: journeyLength.value,
      type: journeyStatusViewing.value,
      search: journeyFilters.value.search
    }
  })
    .then(({ data }: any) => {
      const temp = (data?.value || {}).data || []
      if (Array.isArray(temp)) {
        journeys.value = [...journeys.value, ...temp]
      }
      journeyLoading.value = false
    })
    .catch(() => {
      journeyLoading.value = false
    })
}

const journeyScheduleComponent: any = ref(null)
const scheduleInfoComponent: any = ref(null)
const openScheduleInfo = () => {
  if (selectedJourneys.value.length) {
    return scheduleInfoComponent.value && scheduleInfoComponent.value.show(selectedJourneys.value)
  }
  scheduleInfoComponent.value && scheduleInfoComponent.value.show()
}
const scheduleSettingComponent: any = ref(null)
const openScheduleSetting = () => {
  scheduleSettingComponent.value && scheduleSettingComponent.value.show()
}

const journeyInfoComponent: any = ref(null)
const selectJourney = (journey: any) => {
  journeyScheduleComponent.value && journeyScheduleComponent.value.selectJourney(journey)
}
const openJourney = (journey: any) => {
  selectJourney(journey)
  journeyInfoComponent.value && journeyInfoComponent.value.show(journey._id)
}

const journeyListComponent: any = ref(null)
const handleCheckAll = (val: boolean) => {
  journeyListComponent.value && journeyListComponent.value.checkAll(val)
}

const company = computed(() => $store().system.getCompany)

const setDataVehicleChange = (data: { [key: string]: any }) => {
  journeyScheduleComponent.value.addRow(data.message)
}

const setDataScheduleChange = (data: { [key: string]: any }) => {
  try {
    const tempData = data.message || {}
    const rowId = journeyScheduleComponent.value.getRowById(tempData.data.vehicle._id)
    switch (tempData.type) {
    case 'create':
      if (rowId) {
        journeyScheduleComponent.value.updateAndAddItem(tempData.data.vehicle._id, tempData.data.journey)
      }
      break
    case 'update':
      if (rowId) {
        journeyScheduleComponent.value.updateAndAddItem(tempData.data.vehicle._id, tempData.data.journey)
      }
      break
    case 'delete':
      journeyScheduleComponent.value.deleteItem(tempData.data)
      break
    default:
      return null
    }
  } catch (e: any) {}
}

const handleScheduleChange = (journeys: any[]) => {
  journeyScheduleComponent.value.addItems(journeys)

  refresh()
}

onUnmounted(() => {
  $event.unsubscribe(`c-${company.value._id}-journey-schedule-change`, () => {})
  $event.unsubscribe(`c-${company.value._id}-change-vehicle-change-ready`, () => {})
})

const handleManualSchedule = (item: any) => {
  journeyScheduleComponent.value && journeyScheduleComponent.value.manualSchedule(item)
}

const journeyTabs = [
  {
    key: 'ALL',
    label: 'Tất cả'
  },
  {
    key: 'NotYetDistribution',
    label: 'Chưa điều'
  },
  {
    key: 'ConfirmedDistribution',
    label: 'Điều chính thức'
  }
]

const journeyTabsShowCheckAll = ['NotYetDistribution', 'ScheduleDistribution']
</script>
